import axios from "axios";
import jobDescriptionService from "./job.description.service";
import bulkUploadService from "./bulk.upload.service";
import organizationService from "./organization.service";
import interviewService from "./interview.service";
import { JobDescriptionEntity } from "../Entities/JobDescription";
import { DEFAULT_PASS_PERCENTAGE } from "../Pages/AddJobPostV3/utils/constants";

class UserOnboardingService {
  /**
   * Generates a structured job post from raw text description,
   * then creates and saves it via the job description API.
   * Used for all cases (1-3 auto and 6 form-based).
   * Returns the new job post ID.
   */
  async generateAndCreateJobPost(jobDescription: string): Promise<string> {
    console.log("[UserOnboarding] generateAndCreateJobPost — START");
    console.log("[UserOnboarding]   jobDescription length:", jobDescription?.length, "| preview:", jobDescription?.slice(0, 80));

    const org = await organizationService.getOrganizationData();
    const orgName = org?.orgName || "QualifyMe.ai";
    console.log("[UserOnboarding]   org:", orgName);

    // Step 1: Generate structured job post from raw text
    console.log("[UserOnboarding] Step 1: calling generateJobDescription...");
    const generated = await jobDescriptionService.generateJobDescription(jobDescription);
    console.log("[USerOnboarding] Step 1 ✓ generation complete", " | generated keys:", Object.keys(generated || {}));
    if (!generated) {
      throw new Error("Failed to generate job post from description.");
    }
    //console.log("[UserOnboarding] Step 1 ✓ generated payload keys:", Object.keys(generated));

    // Step 2: Ensure required fields have defaults before saving.
    // The AI-generated payload may omit numberOfOpenings; the backend rejects values < 1.
    if (!generated.numberOfOpenings || generated.numberOfOpenings < 1) {
      console.log("[UserOnboarding]   numberOfOpenings was", generated.numberOfOpenings, "— defaulting to 1");
      generated.numberOfOpenings = 1;
    }
    const allRoundsInDB = await interviewService.getGenericRoundsOnly();
    console.log("[UserOnboarding]   fetched generic rounds from DB:", allRoundsInDB);
    const overallAssessmentRoundInDB = allRoundsInDB.find(
      (round) => round.isGeneric && round.title.trim().replaceAll(/\s+/g, '').toLowerCase() === "overallassessment"
    );
    console.log("[UserOnboarding]   matched overall-assessment round from DB:", overallAssessmentRoundInDB);

    if (!overallAssessmentRoundInDB) {
      throw new Error("Required generic round (overall-assessment) not found in DB.");
    }

    const payload: Partial<JobDescriptionEntity> = {
      title: generated?.title || "",
      position: generated?.position || "",
      numberOfOpenings: 1,
      noticePeriod: 0,
      description: generated?.description || "",
      isProfileMatchRequired: true,
      requirements: (generated?.requirements || []).map(
        (req: any, index: number) => ({
          id: index + 1,
          topic: req.topic || "",
          description: req.description || "",
          mustMatch: req.mustMatch ?? false,
        })
      ),
      additionalRequirements: [],
      interviewRounds: [
      {
        interviewRoundId: overallAssessmentRoundInDB?._id || null,
        interviewRoundNumber: 1,
        isMustQualify: true,
        isAIGenerated: true,
        passPercentage: DEFAULT_PASS_PERCENTAGE, // Include pass percentage if provided
      },
    ],
      interviewers: [],
      interviewGuests: [],
      onlineInterview: false,
    };

    console.log("[UserOnboarding] Step 3 ✓ interviewRounds:", payload)

    // Create and save the job post as active so candidates can be screened against it immediately.
    console.log("[UserOnboarding] Step 4: calling addJobDescription (active)...");


    const created = await jobDescriptionService.addJobDescription(payload, orgName, true);
    if (!created?._id && !created?.id) {
      throw new Error("Failed to save job post.");
    }
    const jobPostId = created._id || created.id;
    console.log("[UserOnboarding] Step 4 ✓ job post created | id:", jobPostId);
    return jobPostId;
  }

  /**
   * Uploads a resume file to an existing job post using the bulk upload flow:
   * 1. Get presigned S3 URL
   * 2. Upload file to S3
   * 3. Trigger backend processing
   * Returns the jobApplicationId from the processing response.
   */
  async uploadResumeToJobPost(
    jobDescriptionId: string,
    resumeFile: File
  ): Promise<string | undefined> {
    console.log("[UserOnboarding] uploadResumeToJobPost — START");
    console.log("[UserOnboarding]   jobDescriptionId:", jobDescriptionId);
    console.log("[UserOnboarding]   file:", resumeFile.name, "| size:", resumeFile.size, "bytes | type:", resumeFile.type);

    // Step 1: Get presigned URL
    console.log("[UserOnboarding] Step A: getUploadPresignedUrl...");
    const presignedData = await bulkUploadService.getUploadPresignedUrl(
      resumeFile.name,
      "application/pdf",
      jobDescriptionId
    );

    if (!presignedData?.url) {
      console.error("[UserOnboarding] Step A ✗ presignedData missing url:", presignedData);
      throw new Error("Failed to get upload URL for resume.");
    }
    console.log("[UserOnboarding] Step A ✓ presigned URL obtained");

    // Step 2: Upload to S3 using the presigned URL (direct PUT, no auth header)
    console.log("[UserOnboarding] Step B: uploading to S3 presigned URL...");
    await axios.put(presignedData.url, resumeFile, {
      headers: { "Content-Type": "application/pdf" },
    });
    console.log("[UserOnboarding] Step B ✓ S3 upload complete");

    // Step 3: Trigger backend processing
    console.log("[UserOnboarding] Step C: startFileProcessing...");
    const processingResp = await bulkUploadService.startJobCreatingProcessForSingleUpload(jobDescriptionId, presignedData?.fileName);
    console.log("[UserOnboarding] Step C ✓ file processing triggered | response data:", processingResp);
    console.log("[UserOnboarding] uploadResumeToJobPost — DONE");

    // Response shape: { status: true, message, job: { jobApplicationId, status } }
    return processingResp?.job?.jobApplicationId;
  }

  /**
   * Triggers backend processing for a resume already uploaded to S3 (session resume).
   * Used when the user selects "use existing resume" — no re-upload needed.
   * Returns the jobApplicationId from the processing response.
   */
  async processExistingSessionResume(
    jobDescriptionId: string,
    resumeFileName: string,
    screeningSessionId?: string
  ): Promise<string | undefined> {
    console.log("[UserOnboarding] processExistingSessionResume — START");
    console.log("[UserOnboarding]   jobDescriptionId:", jobDescriptionId);
    console.log("[UserOnboarding]   resumeFileName:", resumeFileName);
    console.log("[UserOnboarding]   screeningSessionId:", screeningSessionId ?? "(none)");

    // Pass screeningSessionId so the backend can copy the file from the screening
    // session's S3 path to the new job post's S3 path before processing.
    // The backend runs the full pipeline synchronously and responds with:
    // { status: true, message, job: { jobApplicationId, status } }
    // When status is false the operation failed — treat as an error.
    const processingResp = await bulkUploadService.startJobCreatingProcessForSingleUpload(jobDescriptionId, resumeFileName, screeningSessionId);
    console.log("[UserOnboarding] processExistingSessionResume ✓ response data:", processingResp);

    if (processingResp?.status === false) {
      throw new Error(processingResp?.message || "Resume processing failed. Please try again.");
    }

    return processingResp?.job?.jobApplicationId;
  }
}

const userOnboardingService = new UserOnboardingService();
export default userOnboardingService;
